import React from 'react';
import type { Column } from '../components';
import type { HeaderGroup } from '../components/RankedTable';
import type { Period } from '../api/types';
import { PERIOD_LABELS } from '../utils/periods';
import { formatMoney, moneyLabel } from '../utils/format';

/**
 * Sales column(s) for a ranked table. When the API returns a compare period
 * (Today -> + WTD, MTD -> WTD + MTD) two columns are shown side by side, else one.
 */
export function salesColumns<T>(
  period: Period,
  comparePeriod: Period | null | undefined,
  sales: (r: T) => number,
  compareSales: (r: T) => number | null | undefined,
  width: number,
  /** Put the unit on a second line under the header (compact phone layout). */
  stackUnit = false,
): Column<T>[] {
  const col = (key: string, header: React.ReactNode, value: (r: T) => number | null | undefined): Column<T> => ({
    key, header, align: 'right', width, render: (r) => formatMoney(value(r)),
  });
  if (!comparePeriod) return [col('sales', stackUnit ? <>Sales<br />{moneyLabel()}</> : `Sales (${moneyLabel()})`, sales)];
  // Unit goes on a second line so the numeric columns can stay narrow.
  const head = (p: Period) => <>{PERIOD_LABELS[p]}<br />{moneyLabel()}</>;
  const main = col('sales', head(period), sales);
  const extra = col('compare', head(comparePeriod), compareSales);
  // Reads oldest-to-newest scope: Today | WTD, WTD | MTD.
  return period === 'mtd' ? [extra, main] : [main, extra];
}

/**
 * Grouped columns: each period gets its own Sales + YoY under one merged header ("TODAY ₹L / YoY").
 * Order reads Today | WTD and WTD | MTD. Returns the group bands to pass as `headerGroups`.
 */
export function periodGroups<T>(
  period: Period,
  comparePeriod: Period,
  main: { sales: (r: T) => number | null | undefined; yoy: (r: T) => number | null | undefined },
  extra: { sales: (r: T) => number | null | undefined; yoy: (r: T) => number | null | undefined },
  salesWidth: number,
  yoyWidth: number,
  renderYoy: (v: number | null | undefined) => React.ReactNode,
): { columns: Column<T>[]; groups: HeaderGroup[] } {
  const cols = (p: Period, g: typeof main): Column<T>[] => [
    { key: `${p}-sales`, header: `Sales ${moneyLabel()}`, align: 'right', width: salesWidth, render: (r) => formatMoney(g.sales(r)) },
    { key: `${p}-yoy`, header: 'YoY', align: 'right', width: yoyWidth, render: (r) => renderYoy(g.yoy(r)) },
  ];
  const ordered: [Period, typeof main, 'amber' | 'violet'][] =
    period === 'mtd' ? [[comparePeriod, extra, 'amber'], [period, main, 'violet']]
                     : [[period, main, 'amber'], [comparePeriod, extra, 'violet']];
  return {
    columns: ordered.flatMap(([p, g]) => cols(p, g)),
    groups: ordered.map(([p, , tone]) => ({ label: `${PERIOD_LABELS[p].toUpperCase()} ${moneyLabel().replace(' ', '')} / YoY`, span: 2, tone })),
  };
}
